/**
 * Optional OpenAI narrative on top of rule-based tuning JSON.
 * Signed-in users: Firebase Callable `tuningInsight` (server holds OPENAI_API_KEY).
 * Otherwise: browser or dev proxy (BYOK / REACT_APP_OPENAI_API_KEY — not secret in prod).
 */

import '../firebase';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from 'firebase/functions';

const SYSTEM_PROMPT = `You are a mountain bike suspension tuning assistant. You MUST:
- Base reasoning on the JSON run data and the "ruleBasedHighlights" list (treat those as ground truth signals).
- Use this knowledge summary: Rebound p95 velocity ~1000–1400 mm/s is a common HSR reference; compression p95 is trail-dependent (broadly ~2500–4000 mm/s) and supports HSC diagnosis only alongside depth and bottom-outs. Rebound velocity should usually stay below compression velocity; if rebound p95 ≥ compression p95, rebound may be too fast. Many bottom-outs often mean spring too soft or insufficient progression; very low travel use with zero bottom-outs may mean spring too stiff or sag too high. Rear top-out count should normally stay below front; if rear ≥ front, check rear spring softness or front rebound too fast. Packing (compression strokes ≫ rebound strokes) can mean rebound too slow.
- Give concrete next checks (what to adjust first: spring/sag vs LSC/LSR vs HSC/HSR) without naming specific spring SKUs.
- Include a dedicated section named "Specific tuning actions" with concise bullet actions using explicit direction words (increase/decrease/add/remove). Cover only actions justified by the provided data and choose from: low-speed rebound, high-speed rebound, low-speed compression, high-speed compression, add/remove volume tokens (or spacers), increase/decrease air pressure or spring rate.
- If data is missing, say what is missing instead of inventing numbers.
- Keep a clear structure: (1) Overview (2) Front (3) Rear (4) Prioritized actions (5) Specific tuning actions (6) What to verify on the next run.`;

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Lightweight markdown-ish lines → HTML for AI insight blocks */
export function formatOpenAiInsightHtml(text) {
  const lines = String(text || '').split(/\r?\n/);
  const parts = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i += 1;
      continue;
    }
    if (/^#{1,6}\s+/.test(line)) {
      const heading = line.replace(/^#{1,6}\s+/, '');
      parts.push(`<h3>${escapeHtml(heading)}</h3>`);
      i += 1;
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      parts.push(`<ol>${items.map((it) => `<li>${escapeHtml(it)}</li>`).join('')}</ol>`);
      continue;
    }
    if (/^- /.test(line)) {
      const items = [];
      while (i < lines.length && /^- /.test(lines[i].trim())) {
        const raw = lines[i].trim().replace(/^- /, '');
        const boldMatch = raw.match(/^\*\*(.+?)\*\*:\s*(.*)$/);
        if (boldMatch) {
          items.push(`<strong>${escapeHtml(boldMatch[1])}:</strong> ${escapeHtml(boldMatch[2])}`);
        } else {
          items.push(escapeHtml(raw));
        }
        i += 1;
      }
      parts.push(`<ul>${items.map((it) => `<li>${it}</li>`).join('')}</ul>`);
      continue;
    }
    const paragraphLines = [line];
    i += 1;
    while (i < lines.length) {
      const next = lines[i].trim();
      if (!next || /^#{1,6}\s+/.test(next) || /^\d+\.\s+/.test(next) || /^- /.test(next)) break;
      paragraphLines.push(next);
      i += 1;
    }
    const paragraphText = paragraphLines.join(' ');
    const boldLabelMatch = paragraphText.match(/^\*\*(.+?)\*\*:\s*(.*)$/);
    if (boldLabelMatch) {
      parts.push(`<p><strong>${escapeHtml(boldLabelMatch[1])}:</strong> ${escapeHtml(boldLabelMatch[2])}</p>`);
    } else {
      parts.push(`<p>${escapeHtml(paragraphText)}</p>`);
    }
  }
  return parts.join('');
}

export function resolveOpenAiApiKey(tuningConfig) {
  const k = tuningConfig?.tuneOpenaiApiKey;
  if (k && String(k).trim()) return String(k).trim();
  const env = process.env.REACT_APP_OPENAI_API_KEY;
  return env && String(env).trim() ? String(env).trim() : '';
}

function shouldUseFunctionsEmulator() {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV !== 'development') return false;
  if (process.env.REACT_APP_FIREBASE_FUNCTIONS_USE_EMULATOR !== 'true') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

function callableFallbackCodes(code) {
  return (
    code === 'functions/not-found' ||
    code === 'functions/failed-precondition' ||
    code === 'functions/unavailable'
  );
}

/**
 * @param {string} apiKey — optional; BYOK / env for unsigned users or dev fallback
 * @param {object} payload — tuning JSON for the model
 */
export async function fetchOpenAiTuningInsight(apiKey, payload) {
  const forceClient = process.env.REACT_APP_TUNING_INSIGHT_FORCE_CLIENT === 'true';

  if (!forceClient) {
    const app = getApp();
    const auth = getAuth(app);
    if (auth.currentUser) {
      const region = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'us-central1';
      const functions = getFunctions(app, region);
      if (shouldUseFunctionsEmulator()) {
        try {
          connectFunctionsEmulator(functions, '127.0.0.1', 5001);
        } catch (_e) {
          /* already connected */
        }
      }
      const callable = httpsCallable(functions, 'tuningInsight');
      const model = process.env.REACT_APP_OPENAI_TUNING_MODEL || 'gpt-4o-mini';
      try {
        const result = await callable({ payload, model });
        const text = result.data?.text;
        if (!text || typeof text !== 'string') {
          throw new Error('Empty response from tuning insight service.');
        }
        return text.trim();
      } catch (e) {
        const code = e?.code;
        if (apiKey && callableFallbackCodes(code)) {
          /* fall through to client-side path */
        } else {
          throw new Error(e?.message || String(e));
        }
      }
    }
  }

  if (!apiKey) {
    throw new Error(
      'Sign in to generate tuning insight, or add an OpenAI API key under Settings → Advanced (Summary tuning guide), or set REACT_APP_OPENAI_API_KEY for local unsigned use.',
    );
  }

  const model = process.env.REACT_APP_OPENAI_TUNING_MODEL || 'gpt-4o-mini';
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const endpoint = isLocalhost ? '/openai/chat/completions' : 'https://api.openai.com/v1/chat/completions';
  const headers = {
    'Content-Type': 'application/json',
  };
  if (isLocalhost) {
    headers['x-openai-api-key'] = apiKey;
  } else {
    headers.Authorization = `Bearer ${apiKey}`;
  }
  const res = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model,
      temperature: 0.35,
      max_tokens: 1400,
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        {
          role: 'user',
          content: `Suspension run JSON (values in mm, mm/s, % as noted):\n${JSON.stringify(payload, null, 2)}`,
        },
      ],
    }),
  });
  if (!res.ok) {
    let msg = `OpenAI HTTP ${res.status}`;
    try {
      const errJson = await res.json();
      if (errJson?.error?.message) msg = errJson.error.message;
    } catch (_e) {
      try {
        const t = await res.text();
        if (t) msg = t.slice(0, 400);
      } catch (_e2) {
        /* ignore */
      }
    }
    throw new Error(msg);
  }
  const data = await res.json();
  const text = data?.choices?.[0]?.message?.content;
  if (!text || typeof text !== 'string') throw new Error('Empty response from OpenAI');
  return text.trim();
}
